import { type Ref, watch } from "vue";

/**
 * Stale-while-revalidate keeps the previously-rendered entity on screen while
 * the next fetch runs, so refetching the *same* entity (a background sync, a
 * revision switch) never blanks the page and the deferred spinner never flashes.
 *
 * But when the user navigates to a *different* entity the previous content is no
 * longer merely stale — it is wrong. Rendering it makes the new page briefly
 * show the old entity (most visibly the old BPMN diagram when switching between
 * process definitions). In that case the content must be dropped up front and
 * the deferred spinner cover the gap until the new entity loads.
 *
 * @param loadedId identity of the entity currently rendered (undefined on a
 *   cold load).
 * @param nextId identity of the entity about to load.
 * @returns true when loaded content belongs to a *different* entity than the
 *   one about to load (it should be cleared); false on a same-entity refetch or
 *   a cold load (nothing to drop).
 */
export const shouldDropStaleContent = (
    loadedId: string | undefined,
    nextId: string | undefined
): boolean => loadedId !== undefined && loadedId !== nextId;

/**
 * Wire stale-while-revalidate cleanup into an entity store. Blanks the given
 * content refs the moment the routed entity changes to a *different* one, so the
 * previous entity's content is never rendered on the new entity's page.
 *
 * Keying purely on the routed identity makes this a no-op on a cold load and on
 * same-entity refetches (revision switch / background sync) — exactly the cases
 * where stale-while-revalidate should hold the content to avoid a spinner flash.
 *
 * @param currentEntityId getter for the routed entity's identity.
 * @param contentRefs the refs that render the entity (entity, description,
 *   definition, …) — cleared together on a genuine switch.
 */
export const clearStaleContentOnSwitch = (
    currentEntityId: () => string | undefined,
    contentRefs: Ref<unknown>[]
): void => {
    watch(currentEntityId, (nextId, loadedId) => {
        if (shouldDropStaleContent(loadedId, nextId)) {
            for (const contentRef of contentRefs) {
                contentRef.value = undefined;
            }
        }
    });
};
